import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError, type HealthState } from '../lib/api'
import { fmtAgo, fmtDateTime, fmtFull, fmtMoney, fmtNumber } from '../lib/format'
import { useAuth } from '../lib/auth'
import { Card, Empty, Field, GroupChip, HealthPill, Loading, Modal, SignalBars, StatusPill } from '../components/ui'
import { AtConsoleCard, FrameTraceCard, InboundCard, TestSendCard } from '../components/DeviceConsole'

interface DeviceDetailData {
  id: string
  deviceId: string
  name: string
  label: string | null
  msisdn: string | null
  operatorProfileId: string | null
  approvalState: string
  healthState: HealthState
  darkReason: string | null
  darkSince: string | null
  nextProbeAt: string | null
  isEnabled: boolean
  isConnected: boolean
  connectedAt: string | null
  lastHeartbeatAt: string | null
  remoteIp: string | null
  rssi: number | null
  ber: number | null
  registrationStatus: number | null
  operatorName: string | null
  modemModel: string | null
  imei: string | null
  iccid: string | null
  imsi: string | null
  balanceAmount: number | null
  balanceCurrency: string | null
  balanceExpiresOn: string | null
  balanceUpdatedAt: string | null
  firmwareVersion: string | null
  lastBootAt: string | null
  bootCount: number
  dispatchCounter: number
  weight: number
  maxInFlight: number
  rateLimitPerMinute: number
  dailyCap: number
  lowBalanceThreshold: number | null
  criticalBalanceThreshold: number | null
  hardFloor: number
  groups: { id: string; name: string; colour: string | null }[]
  events: { id: string; type: string; severity: string; summary: string; at: string }[]
  balances: {
    requestedAt: string
    /** Null while the modem has not answered yet — this is what drives the "checking" state. */
    respondedAt: string | null
    parsedAmount: number | null
    parsedCurrency: string | null
    ok: boolean
    error: string | null
    /** Only sent for failures — what the operator actually replied, so the regex can be fixed. */
    rawResponse: string | null
  }[]
  recentDeliveries: { id: string; msisdn: string; status: string; createdAt: string; reasonCode: string | null }[]
}

/**
 * How long an unanswered balance check is still worth waiting for. The USSD timeout on the device
 * is 20 s by profile; past a minute the modem is not going to answer this one, and saying so beats
 * a spinner that never stops.
 */
const BALANCE_GRACE_MS = 60_000

/** True while the most recent balance check is open and still within its grace period. */
function isAwaiting(check?: { requestedAt: string; respondedAt: string | null }): boolean {
  if (!check || check.respondedAt !== null) return false
  return Date.now() - new Date(check.requestedAt).getTime() < BALANCE_GRACE_MS
}

export default function DeviceDetail() {
  const { id } = useParams<{ id: string }>()
  const qc = useQueryClient()
  const { can } = useAuth()
  const [note, setNote] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [editingIdentity, setEditingIdentity] = useState(false)

  const operators = useQuery({
    queryKey: ['operator-profiles'],
    queryFn: () => api.get<{ id: string; name: string; isVerified: boolean }[]>('/api/admin/operator-profiles'),
  })

  const device = useQuery({
    queryKey: ['device', id],
    queryFn: () => api.get<DeviceDetailData>(`/api/admin/devices/${id}`),
    // Poll fast while a balance check is open: the modem's answer is the event being waited on,
    // and a USSD round trip is seconds, not the 5 s idle cadence.
    refetchInterval: (query) => (isAwaiting(query.state.data?.balances?.[0]) ? 2_000 : 5_000),
  })

  const [config, setConfig] = useState<{
    weight: number
    maxInFlight: number
    rateLimitPerMinute: number
    dailyCap: number
  } | null>(null)

  const [thresholds, setThresholds] = useState<{
    low: string
    critical: string
    hardFloor: string
  } | null>(null)

  if (device.data && config === null) {
    setConfig({
      weight: device.data.weight,
      maxInFlight: device.data.maxInFlight,
      rateLimitPerMinute: device.data.rateLimitPerMinute,
      dailyCap: device.data.dailyCap,
    })
  }

  if (device.data && thresholds === null) {
    setThresholds({
      // Empty string, not 0 — blank means "use the global default", which is a different
      // instruction from "alert me when the balance reaches zero".
      low: device.data.lowBalanceThreshold?.toString() ?? '',
      critical: device.data.criticalBalanceThreshold?.toString() ?? '',
      hardFloor: device.data.hardFloor.toString(),
    })
  }

  const act = useMutation({
    mutationFn: async ({ path, body, query, label }: { path: string; body?: unknown; query?: Record<string, string | number | boolean>; label: string }) => {
      await api.post(`/api/admin/devices/${id}/${path}`, body, query)
      return label
    },
    onSuccess: (label) => {
      setNote(label)
      setError(null)
      void qc.invalidateQueries({ queryKey: ['device', id] })
      void qc.invalidateQueries({ queryKey: ['devices'] })
    },
    onError: (err) => {
      setNote(null)
      setError(err instanceof ApiError ? err.message : 'That did not work.')
    },
  })

  const saveConfig = useMutation({
    mutationFn: () => api.put(`/api/admin/devices/${id}/config`, config),
    onSuccess: () => {
      setNote('Configuration saved.')
      void qc.invalidateQueries({ queryKey: ['device', id] })
    },
  })

  const saveThresholds = useMutation({
    mutationFn: () =>
      api.put(`/api/admin/devices/${id}/balance-thresholds`, {
        low: thresholds?.low.trim() ? Number(thresholds.low) : null,
        critical: thresholds?.critical.trim() ? Number(thresholds.critical) : null,
        hardFloor: Number(thresholds?.hardFloor || 0),
      }),
    onSuccess: () => {
      setNote('Balance thresholds saved.')
      setError(null)
      void qc.invalidateQueries({ queryKey: ['device', id] })
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Could not save thresholds.'),
  })

  if (device.isLoading || !device.data || !config) return <Loading rows={8} />
  const d = device.data
  const operatorName = operators.data?.find((o) => o.id === d.operatorProfileId)?.name ?? null

  const lastCheck = d.balances[0] ?? null
  const awaitingBalance = isAwaiting(lastCheck)
  // An open row past the grace period means the modem never answered. The server writes the row
  // off on its own schedule; the page says so as soon as it is true rather than waiting for it.
  const balanceUnanswered = lastCheck !== null && lastCheck.respondedAt === null && !awaitingBalance

  return (
    <div className="stack" style={{ gap: 18 }}>
      <div className="page-head">
        <div>
          <Link to="/devices" className="faint small">
            ← Devices
          </Link>
          <h1 style={{ marginTop: 6 }}>{d.name}</h1>
          <div className="row wrap" style={{ gap: 8, marginTop: 8 }}>
            <HealthPill state={d.healthState} reason={d.darkReason} />
            {!d.isEnabled && <span className="chip">disabled</span>}
            {d.groups.map((g) => (
              <GroupChip key={g.id} name={g.name} colour={g.colour} />
            ))}
          </div>
        </div>
      </div>

      {note && <div className="banner ok">{note}</div>}
      {error && <div className="banner danger">{error}</div>}

      {d.healthState === 'Dark' && (
        <div className="banner danger">
          <div>
            <strong>Dark since {fmtAgo(d.darkSince)}</strong> — reason <code>{d.darkReason}</code>.
            {d.nextProbeAt && <> Next probe {fmtAgo(d.nextProbeAt)}.</>} This device is excluded from routing until
            it recovers.
          </div>
        </div>
      )}

      <div className="grid cols-4">
        <Card title="Signal">
          <SignalBars rssi={d.rssi} />
          <div className="faint small" style={{ marginTop: 6 }}>
            {d.operatorName ?? 'unknown operator'} · reg {d.registrationStatus ?? '—'}
          </div>
        </Card>
        <Card title="Balance">
          <div className="stat-value" style={{ fontSize: 21 }}>
            {fmtMoney(d.balanceAmount, d.balanceCurrency)}
          </div>
          <div className="faint small">
            {d.balanceUpdatedAt ? `checked ${fmtAgo(d.balanceUpdatedAt)}` : 'never checked'}
            {d.balanceExpiresOn && ` · expires ${d.balanceExpiresOn}`}
          </div>
          {/* The outcome of the last check, which is the only place a parse failure is visible.
              Without it a check that dialled, answered and failed to parse looked identical to one
              that was never requested. */}
          {awaitingBalance && (
            <div className="field-hint" style={{ marginTop: 8 }}>
              Checking with the modem…
            </div>
          )}
          {balanceUnanswered && (
            <div className="field-hint" style={{ marginTop: 8 }}>
              The device did not answer the check requested {fmtAgo(lastCheck!.requestedAt)}.
            </div>
          )}
          {lastCheck && lastCheck.respondedAt !== null && !lastCheck.ok && (
            <div className="field-hint" style={{ marginTop: 8 }}>
              Last check failed: {lastCheck.error ?? 'the response could not be read'}
              {/* The reply itself, because "the regex did not match" is only actionable next to
                  the text it did not match. This is the pattern-writing loop: read it here, paste
                  it into Settings → Test the pattern, save. */}
              {lastCheck.rawResponse && (
                <pre
                  className="mono small"
                  style={{ margin: '6px 0 0', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                >
                  {lastCheck.rawResponse}
                </pre>
              )}
            </div>
          )}
        </Card>
        <Card title="Lifetime sent">
          <div className="stat-value" style={{ fontSize: 21 }}>
            {fmtNumber(d.dispatchCounter)}
          </div>
          <div className="faint small">{d.bootCount} boots</div>
        </Card>
        <Card title="Connection">
          <div className="stat-value" style={{ fontSize: 21 }}>
            {d.isConnected ? 'online' : 'offline'}
          </div>
          <div className="faint small">
            {d.isConnected ? `since ${fmtAgo(d.connectedAt)}` : `last seen ${fmtAgo(d.lastHeartbeatAt)}`}
          </div>
        </Card>
      </div>

      <div className="grid cols-2">
        <Card
          title="Identity"
          actions={
            can('Admin') && (
              <button className="btn sm" onClick={() => setEditingIdentity(true)}>
                Edit
              </button>
            )
          }
        >
          <dl className="kv">
            <dt>Device ID</dt>
            <dd className="mono small">{d.deviceId}</dd>
            <dt>MSISDN</dt>
            <dd className="mono">{d.msisdn ?? '—'}</dd>
            <dt>Label</dt>
            <dd>{d.label ?? '—'}</dd>
            <dt>Operator profile</dt>
            <dd>
              {operatorName ?? (
                <span className="faint">
                  none — balance checks are unavailable until one is set
                </span>
              )}
            </dd>
            <dt>IMEI</dt>
            <dd className="mono small">{d.imei ?? '—'}</dd>
            <dt>ICCID</dt>
            <dd className="mono small">{d.iccid ?? '—'}</dd>
            <dt>Modem</dt>
            <dd>{d.modemModel ?? '—'}</dd>
            <dt>Firmware</dt>
            <dd>{d.firmwareVersion ?? '—'}</dd>
            <dt>Remote IP</dt>
            <dd className="mono small">{d.remoteIp ?? '—'}</dd>
            <dt>Last boot</dt>
            <dd>{fmtFull(d.lastBootAt)}</dd>
          </dl>
        </Card>

        <Card title="Routing configuration">
          <div className="grid cols-2" style={{ gap: 14 }}>
            <Field label="Weight" hint="Share of traffic relative to other devices.">
              <input
                type="number"
                step="0.1"
                value={config.weight}
                onChange={(e) => setConfig({ ...config, weight: Number(e.target.value) })}
                disabled={!can('Admin')}
              />
            </Field>
            <Field label="Max in flight" hint="1 is right for a SIM800L.">
              <input
                type="number"
                value={config.maxInFlight}
                onChange={(e) => setConfig({ ...config, maxInFlight: Number(e.target.value) })}
                disabled={!can('Admin')}
              />
            </Field>
            <Field label="Rate limit / minute" hint="Guards against operator anti-spam.">
              <input
                type="number"
                value={config.rateLimitPerMinute}
                onChange={(e) => setConfig({ ...config, rateLimitPerMinute: Number(e.target.value) })}
                disabled={!can('Admin')}
              />
            </Field>
            <Field label="Daily cap">
              <input
                type="number"
                value={config.dailyCap}
                onChange={(e) => setConfig({ ...config, dailyCap: Number(e.target.value) })}
                disabled={!can('Admin')}
              />
            </Field>
          </div>
          {can('Admin') && (
            <button className="btn primary sm" onClick={() => saveConfig.mutate()} disabled={saveConfig.isPending}>
              Save configuration
            </button>
          )}
        </Card>

        <Card title="Balance thresholds">
          <div className="small muted" style={{ marginBottom: 12 }}>
            Leave low or critical blank to use the fleet-wide defaults. The hard floor has no
            default: below it this SIM stops being routable, which is what keeps a campaign from
            running a card down to zero and failing mid-send.
          </div>
          <div className="grid cols-3" style={{ gap: 16 }}>
            <Field label="Low balance alert" hint="Blank = fleet default.">
              <input
                type="number"
                step="0.01"
                value={thresholds?.low ?? ''}
                onChange={(e) => thresholds && setThresholds({ ...thresholds, low: e.target.value })}
                disabled={!can('Admin')}
                placeholder="fleet default"
              />
            </Field>
            <Field label="Critical balance alert" hint="Blank = fleet default.">
              <input
                type="number"
                step="0.01"
                value={thresholds?.critical ?? ''}
                onChange={(e) => thresholds && setThresholds({ ...thresholds, critical: e.target.value })}
                disabled={!can('Admin')}
                placeholder="fleet default"
              />
            </Field>
            <Field label="Hard floor" hint="Stops routing to this SIM at or below this balance.">
              <input
                type="number"
                step="0.01"
                value={thresholds?.hardFloor ?? '0'}
                onChange={(e) => thresholds && setThresholds({ ...thresholds, hardFloor: e.target.value })}
                disabled={!can('Admin')}
              />
            </Field>
          </div>
          {can('Admin') && (
            <button
              className="btn primary sm"
              onClick={() => saveThresholds.mutate()}
              disabled={saveThresholds.isPending}
            >
              Save thresholds
            </button>
          )}
        </Card>
      </div>

      <Card title="Actions">
        <div className="btn-row">
          <button
            className="btn"
            onClick={() => act.mutate({ path: 'diagnostics', label: 'Diagnostics requested.' })}
            disabled={!can('Operator') || !d.isConnected}
          >
            Run diagnostics
          </button>
          <button
            className="btn"
            onClick={() => act.mutate({ path: 'balance-check', label: 'Balance check requested.' })}
            disabled={!can('Operator') || !d.isConnected || act.isPending || awaitingBalance}
          >
            {awaitingBalance ? 'Checking…' : 'Check balance'}
          </button>
          <button
            className="btn"
            onClick={() =>
              act.mutate({
                path: 'enabled',
                // The endpoint reads `enabled` from the query string. Without it the request is
                // rejected with a 400 before it reaches the handler, which is why this button
                // never did anything.
                query: { enabled: !d.isEnabled },
                label: d.isEnabled ? 'Device disabled.' : 'Device enabled.',
              })
            }
            disabled={!can('Admin')}
          >
            {d.isEnabled ? 'Disable' : 'Enable'}
          </button>
          <button
            className="btn"
            onClick={() => act.mutate({ path: 'reissue-token', label: 'A new token is held for 10 minutes.' })}
            disabled={!can('Admin')}
            title="Use after a reflash that erased NVS, or if the service restarted before the device first connected."
          >
            Re-issue enrolment token
          </button>
          <button
            className="btn"
            onClick={() => act.mutate({ path: 'reboot', label: 'Reboot sent.' })}
            disabled={!can('Admin') || !d.isConnected}
          >
            Reboot
          </button>
          <button
            className="btn danger"
            onClick={() => act.mutate({ path: 'revoke', label: 'Token revoked. The device can no longer connect.' })}
            disabled={!can('Owner')}
          >
            Revoke token
          </button>
        </div>
        {!d.isConnected && (
          <div className="field-hint" style={{ marginTop: 10 }}>
            Commands that talk to the modem need the device to be connected.
          </div>
        )}
      </Card>

      <div className="grid cols-2">
        <TestSendCard deviceId={d.id} deviceName={d.name} connected={d.isConnected} />
        <AtConsoleCard deviceId={d.id} connected={d.isConnected} />
      </div>

      <InboundCard deviceId={d.id} />

      <FrameTraceCard deviceId={d.id} />

      <div className="grid cols-2">
        <Card title="Recent events" flush>
          {d.events.length === 0 ? (
            <Empty title="No events" />
          ) : (
            <div className="table-wrap" style={{ maxHeight: 320, overflow: 'auto' }}>
              <table>
                <tbody>
                  {d.events.map((e) => (
                    <tr key={e.id}>
                      <td className="faint small" style={{ whiteSpace: 'nowrap' }}>
                        {fmtDateTime(e.at)}
                      </td>
                      <td className="mono small">{e.type}</td>
                      <td className="muted small">{e.summary}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card title="Recent messages" flush>
          {d.recentDeliveries.length === 0 ? (
            <Empty title="Nothing sent yet" />
          ) : (
            <div className="table-wrap" style={{ maxHeight: 320, overflow: 'auto' }}>
              <table>
                <tbody>
                  {d.recentDeliveries.map((r) => (
                    <tr key={r.id}>
                      <td className="faint small" style={{ whiteSpace: 'nowrap' }}>
                        {fmtDateTime(r.createdAt)}
                      </td>
                      <td className="mono small">{r.msisdn}</td>
                      <td>
                        <StatusPill status={r.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {editingIdentity && (
        <IdentityModal
          device={d}
          operators={operators.data ?? []}
          onClose={() => setEditingIdentity(false)}
          onSaved={() => {
            setEditingIdentity(false)
            setNote('Identity updated.')
            void qc.invalidateQueries({ queryKey: ['device', id] })
            void qc.invalidateQueries({ queryKey: ['devices'] })
          }}
        />
      )}
    </div>
  )
}

/** Corrects the fields captured at approval time, which are all optional but the name. */
function IdentityModal({
  device,
  operators,
  onClose,
  onSaved,
}: {
  device: DeviceDetailData
  operators: { id: string; name: string; isVerified: boolean }[]
  onClose: () => void
  onSaved: () => void
}) {
  const [name, setName] = useState(device.name)
  const [label, setLabel] = useState(device.label ?? '')
  const [msisdn, setMsisdn] = useState(device.msisdn ?? '')
  const [operatorProfileId, setOperator] = useState(device.operatorProfileId ?? '')
  const [error, setError] = useState<string | null>(null)

  const save = useMutation({
    mutationFn: () =>
      api.put(`/api/admin/devices/${device.id}/identity`, {
        name,
        label: label || null,
        msisdn: msisdn || null,
        operatorProfileId: operatorProfileId || null,
      }),
    onSuccess: onSaved,
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Could not save.'),
  })

  return (
    <Modal
      title={`Edit ${device.name}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" onClick={() => save.mutate()} disabled={!name.trim() || save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {error && <div className="banner danger">{error}</div>}

      <Field label="Device ID" hint="The device's own identity. Not editable.">
        <input value={device.deviceId} disabled className="mono" />
      </Field>
      <Field label="Name">
        <input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
      </Field>
      <Field label="Label" hint="Where the unit physically lives.">
        <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Rack A, shelf 2" />
      </Field>
      <Field label="MSISDN" hint="The SIM's own number. Used by inbound routing and support.">
        <input value={msisdn} onChange={(e) => setMsisdn(e.target.value)} placeholder="+230 5400 0001" className="mono" />
      </Field>
      <Field label="Operator profile" hint="Drives the USSD balance check. Without one, balance checks are refused.">
        <select value={operatorProfileId} onChange={(e) => setOperator(e.target.value)}>
          <option value="">— none —</option>
          {operators.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
              {o.isVerified ? '' : ' (unverified)'}
            </option>
          ))}
        </select>
      </Field>
    </Modal>
  )
}
